import {
  Content,
  ContentAudience,
  ContentNode,
  ContentNodeType,
  ContentType,
  TechDomain,
  allowedInternalCodeRepoUrlPrefixes,
  allowedExternalCodeRepoUrlPrefixes,
  CodeRepoReviewStatus,
  ReviewStatus,
} from '@amzn/awscat-pattern-library-service-model';
import constants from '../common/constants';
import { hrefDisplayNames } from '../components/utils/DisplayNames';
import { ContentSchemaSection } from '../model/ContentSchemaSection';
import {
  deepClone,
  descriptionInputMaxLength,
  descriptionInputMinLength,
  getContentTemplateSchema,
  longTitleInputMaxLength,
  longTitleInputMinLength,
  shortTitleInputMaxLength,
  shortTitleInputMinLength,
} from './UtilsApi';
import { getHTML, getText, convertEntities } from '@amzn/awscat-editor';
import { isForInternalContentAudience } from '../components/workflow-viewer/content/ContentUtils';
import { validateAndUpdateLinksInContentData } from '../components/utils/ContentLinksUtils';
import { APGLEntityEntry } from '@amzn/awscat-editor/dist/extensions/apgl-entity';

export const validateContentMetadata = (
  content: Content,
  entities?: { [key: string]: APGLEntityEntry },
) => {
  const { contentMetadata } = content;
  const contentMetadataError = new Map<string, string>();
  const requires = new Map<string, string>([
    ['contentName', constants.LONG_TITLE_LABEL],
    ['contentType', constants.CONTENT_TYPE_LABEL],
    ['description', constants.CONTENT_DESCRIPTION_LABEL],
  ]);

  requires.forEach((value, key) => {
    // @ts-expect-error TS7053 - strict migration
    if (!contentMetadata[key]) {
      contentMetadataError.set(value, constants.REQUIRED);
    }
  });

  // Validate contentName length
  const contentName = contentMetadata.contentName;
  const convertedContentName = entities ? convertEntities(contentName, entities) : contentName;
  if (
    convertedContentName &&
    (convertedContentName.length < longTitleInputMinLength ||
      convertedContentName.length > longTitleInputMaxLength)
  ) {
    contentMetadataError.set(
      constants.LONG_TITLE_LABEL,
      `Must be between ${longTitleInputMinLength} and ${longTitleInputMaxLength} characters`,
    );
  }

  // Validate description length
  const description = contentMetadata.description;
  const convertedDescription = entities ? convertEntities(description, entities) : description;
  if (
    convertedDescription &&
    (convertedDescription.length < descriptionInputMinLength ||
      convertedDescription.length > descriptionInputMaxLength)
  ) {
    contentMetadataError.set(
      constants.CONTENT_DESCRIPTION_LABEL,
      `On the Metadata tab, the Overview must be between ${descriptionInputMinLength} and ${descriptionInputMaxLength} characters`,
    );
  }

  // Validate shortName (short title) length
  const shortName = contentMetadata.contentShortName;
  const convertedShortName = entities ? convertEntities(shortName, entities) : shortName;
  if (
    convertedShortName &&
    (convertedShortName.length < shortTitleInputMinLength ||
      convertedShortName.length > shortTitleInputMaxLength)
  ) {
    contentMetadataError.set(
      constants.SHORT_TITLE_LABEL,
      `Must be between ${shortTitleInputMinLength} and ${shortTitleInputMaxLength} characters`,
    );
  }

  return contentMetadataError;
};

export const validateContentTemplateMetadata = (content: Content) => {
  const { contentMetadata, contentTypeMetadata, templateMetadata } = content;
  const { contentType } = contentMetadata;
  const templateMetadataSchema = getContentTemplateSchema(
    contentType,
    contentTypeMetadata,
    ContentSchemaSection.TemplateMetadata,
  );
  return validateTemplateMetadata(templateMetadataSchema, templateMetadata);
};

export const validateTemplateMetadata = (templateMetadataSchema: any, templateMetadata: any) => {
  const templateValidationError = new Map<string, string>();

  // Validate empty required template metadata
  if (templateMetadataSchema && Array.isArray(templateMetadataSchema.required)) {
    // @ts-expect-error TS7006 - strict migration
    templateMetadataSchema.required.forEach((key) => {
      if (!templateMetadata[key]) {
        const label = templateMetadataSchema.properties[key].label;
        templateValidationError.set(label, constants.REQUIRED);
      }
    });
  }
  // Validate tech domain conflicts
  let { techDomains } = templateMetadata;
  if (Array.isArray(techDomains)) {
    techDomains = techDomains.filter((t) => t !== TechDomain.NotApplicable);
    if (techDomains.includes(templateMetadata?.mainTechDomain)) {
      templateValidationError.set('Tech domains', constants.TECH_DOMAINS_CONFLICTS_ERROR);
    }
  }
  return templateValidationError;
};

export const validateContentData = (content: Content): Map<string, string> => {
  const { contentMetadata, contentData } = content;
  let contentDataValidationError = new Map<string, string>();
  contentDataValidationError = validateContentNode(
    contentData,
    contentMetadata.contentType,
    contentDataValidationError,
  );
  return contentDataValidationError;
};

export const validateContentNode = (
  contentNode: ContentNode,
  contentType: ContentType,
  validationError: Map<string, any>,
) => {
  const { title: titleData, href, nodeType, subContent, contentSections } = contentNode;
  const title = getText(titleData);

  // Pathslugs for strategies should always start with “strategy-“ (QuickSight report requirement)
  // Pathslugs for contents should not be empty or "home" as default (Docs requirement after introducing entities to titles)
  if (nodeType === ContentNodeType.Document) {
    if (contentType === ContentType.Strategy && href && !href.startsWith('strategy-')) {
      validationError.set(
        title,
        'Pathslugs for strategies should always start with “strategy-“ (QuickSight report requirement)',
      );
    } else if (!href || href === 'home') {
      validationError.set(
        title,
        'Pathslugs for contents should not be empty or default value "home". Please enter a customized pathslug.',
      );
    }
  }

  const pageHrefs = subContent.map((sc) => sc.href).filter((i) => i);
  const sectionHrefs = contentSections.map((cs) => cs.href).filter((i) => i);

  // PageHrefs must be unique within a content.
  if (new Set(pageHrefs).size !== pageHrefs.length) {
    validationError.set(title, 'Duplicate page IDs exist');
  }

  // Section hrefs must be unique within a page.
  if (new Set(sectionHrefs).size !== sectionHrefs.length) {
    validationError.set(title, 'Duplicate section IDs exist');
  }

  subContent?.forEach((sc) => validateContentNode(sc, contentType, validationError));
  contentSections?.forEach((cs) => validateContentNode(cs, contentType, validationError));

  if (!isHrefValid(href)) {
    validationError.set(title, `Invalid ${hrefDisplayNames.get(nodeType)}`);
  }

  return validationError;
};

/*
 ** All lowercase
 ** Use hyphens to separate words
 ** Must be alphanumerical - no underscores, periods, or special characters
 ** Must be alphanumerical and yse hyphens to separate words
 */
const isHrefValid = (value: string) => {
  const hrefReg = /^&?([a-z0-9]+(?:-[a-z0-9]+)*)?$/;
  return hrefReg.test(value);
};

export const validateContent = (
  content: Content,
  validateSections: Set<ContentSchemaSection> = new Set(),
  entities?: { [key: string]: APGLEntityEntry },
) => {
  const noErrorMap = new Map<string, string>();

  const contentMetadataError = validateSections.has(ContentSchemaSection.ContentMetadata)
    ? validateContentMetadata(content, entities)
    : noErrorMap;
  const templateValidationError = validateSections.has(ContentSchemaSection.TemplateMetadata)
    ? validateContentTemplateMetadata(content)
    : noErrorMap;
  const contentDataValidationError = validateSections.has(ContentSchemaSection.ContentSections)
    ? validateContentData(content)
    : noErrorMap;
  const contentDataLinkValidationError = validateAndUpdateLinksInContentData(content);
  const isValid =
    contentMetadataError.size === 0 &&
    templateValidationError.size === 0 &&
    contentDataValidationError.size === 0 &&
    contentDataLinkValidationError.length === 0;
  return {
    contentMetadataError,
    templateValidationError,
    contentDataValidationError,
    contentDataLinkValidationError,
    isValid,
  };
};

export const allowedSalesforceLightningUrls = [
  'https://proservecrm.lightning.force.com/lightning/r/',
  'https://proservecrm.lightning.force.com/lightning/r/pse__Proj__c/',
  'https://proservecrm.lightning.force.com/lightning/r/Opportunity/',
  'https://aws-crm.lightning.force.com/lightning/r/',
  'https://aws-crm.lightning.force.com/lightning/r/Opportunity/',
  'https://aws-crm.lightning.force.com/lightning/r/pse__Proj__c/',
];

export const allowdSalesforceUrl = ['https://aws-crm.my.salesforce.com/'];

export const validateSalesforceId = (value: string) => {
  // validate engagement id is atmost 18 characters long and contains at least one alpha and one numeric character
  let validChars = /^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9]{1,18}$/;
  let isSalesforceIdValid = true;
  allowdSalesforceUrl.forEach((allowedUrl) => {
    const engagementId = value.replace(allowedUrl, '');
    if (value.startsWith(allowedUrl)) {
      isSalesforceIdValid = validChars.test(engagementId);
    }
  });
  allowedSalesforceLightningUrls.forEach((lightningUrl) => {
    const engagementId = value.replace(lightningUrl, '');
    if (value.startsWith(lightningUrl)) {
      // lightning salesforce url ends with '/view'
      validChars = /^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9]{1,18}\/view$/;
      isSalesforceIdValid = validChars.test(engagementId);
    }
  });

  return isSalesforceIdValid;
};

/* Code repo validation */

export const validateCodeRepoAudience = (
  updatedContentAudience: ContentAudience,
  updatedContent: Content,
) => {
  const { contentTypeMetadata } = updatedContent;
  const { codeLinks } = contentTypeMetadata;
  const url = codeLinks?.[0]?.url;
  if (
    !!url &&
    ((isForInternalContentAudience(updatedContentAudience) &&
      !allowedInternalCodeRepoUrlPrefixes.some((u) => url.startsWith(u))) ||
      (!isForInternalContentAudience(updatedContentAudience) &&
        !allowedExternalCodeRepoUrlPrefixes.some((u) => url.startsWith(u))))
  ) {
    return constants.CODE_REPO_AUDIENCE_ERROR;
  } else {
    return '';
  }
};

export const isCodeBlockValid = (contentDataText: string) => {
  const WORDS_IN_50_LINES_CODE = 750;
  const codeBlockRegex = /<pre><code>(.*?)<\/code><\/pre>/g;
  const matches = contentDataText.replace(/(\r\n|\n|\r)/gm, ' ').match(codeBlockRegex);
  return (
    matches?.every((match) => {
      const code = match.replace('<pre><code>', '').replace('</code></pre>', '');
      const wordCount = code.trim().split(/\s+/).length;
      return wordCount <= WORDS_IN_50_LINES_CODE;
    }) ?? true
  );
};

const isEpicSection = (section: ContentNode) => {
  return section.id === 'epics';
};

const isTroubleshootingSection = (section: ContentNode) => {
  return section.id === 'troubleshooting';
};

const isStringOrJson = (section: ContentNode) => {
  return typeof section === 'string' || typeof section === 'object';
};

const isEpicCodeBlocksValid = (epicsData: any) => {
  if (Array.isArray(epicsData) && epicsData.length > 0) {
    return epicsData.every((epic) => {
      const tasks = epic.tasks;
      if (Array.isArray(tasks) && tasks.length > 0) {
        return tasks.every((task) => {
          const taskDescription = getHTML(task.description);
          return isCodeBlockValid(taskDescription);
        });
      }
      return true;
    });
  }
  return true;
};

const isTroubleshootingCodeBlocksValid = (troubleshootingData: any) => {
  if (Array.isArray(troubleshootingData) && troubleshootingData.length > 0) {
    return troubleshootingData.every((troubleshooting) => {
      const issue = getHTML(troubleshooting.issue);
      const solution = getHTML(troubleshooting.solution);
      return isCodeBlockValid(issue) && isCodeBlockValid(solution);
    });
  }
  return true;
};

export const validateContentForCodeBlocks = (content: Content) => {
  const clonedContent = deepClone(content);
  const sectionStack: ContentNode[] = []; // for iterative DFS

  // push subcontent onto sectionStack in reverse order
  clonedContent.contentData.subContent
    .reverse()
    // @ts-expect-error TS7006 - strict migration
    .forEach((subContent) => sectionStack.push(subContent));

  // push content sections onto sectionStack in reverse order
  clonedContent.contentData.contentSections
    .reverse()
    // @ts-expect-error TS7006 - strict migration
    .forEach((section) => sectionStack.push(section));

  while (sectionStack.length > 0) {
    // pop from sectionStack and validate
    // @ts-expect-error TS2322 - strict migration
    const currentSection: ContentNode = sectionStack.pop();
    const { contentData } = currentSection;

    if (!!contentData) {
      if (isEpicSection(currentSection) || isTroubleshootingSection(currentSection)) {
        if (Array.isArray(contentData) && contentData.length > 0) {
          if (
            (isEpicSection(currentSection) && !isEpicCodeBlocksValid(contentData)) ||
            (isTroubleshootingSection(currentSection) &&
              !isTroubleshootingCodeBlocksValid(contentData))
          ) {
            return constants.CODE_BLOCK_VALIDATION_ERROR;
          }
        }
      } else if (isStringOrJson(currentSection)) {
        if (!isCodeBlockValid(getHTML(contentData))) {
          return constants.CODE_BLOCK_VALIDATION_ERROR;
        }
      }
    }

    // push popped section's subcontent into stack in reverse order
    currentSection.subContent.reverse().forEach((subContent) => sectionStack.push(subContent));

    // push popped section's content sections into stack in reverse order
    currentSection.contentSections.reverse().forEach((section) => sectionStack.push(section));
  }
  return '';
};

export const isCodeInvalid = (codeRepoValidationError: string[]) => {
  return (
    Array.isArray(codeRepoValidationError) &&
    codeRepoValidationError.length > 0 &&
    codeRepoValidationError.some((e) => !!e)
  );
};

export const validateCodeReview = (contentAudience: ContentAudience, content: Content) => {
  const { contentTypeMetadata } = content;
  const { codeLinks } = contentTypeMetadata;
  const codeLink = codeLinks?.[0];
  const appsecTicket = codeLink?.appsecTicket;
  const appsecStatus = codeLink?.appsecStatus;
  const openSourceTicket = codeLink?.openSourceTicket;
  const openSourceStatus = codeLink?.openSourceStatus;
  const approvedTicketStatus = [
    ReviewStatus.Approved as string,
    CodeRepoReviewStatus.ResolvedSuccessful as string,
  ];
  const appSecTicketNotResolved =
    !appsecTicket || !appsecStatus || !approvedTicketStatus.includes(appsecStatus);
  const openSourceTicketNotResolvedForExternalContent =
    !isForInternalContentAudience(contentAudience) &&
    (!openSourceTicket || !openSourceStatus || !approvedTicketStatus.includes(openSourceStatus));
  // return validation error when
  // 1. there is code link, and
  // 2. there is no app sec review link, or review not completed, or
  // 3. if external, there is no open source review link, or review not completed
  if (!!codeLink && (appSecTicketNotResolved || openSourceTicketNotResolvedForExternalContent)) {
    return constants.CODE_REVIEW_VALIDATION_ERROR;
  } else {
    return '';
  }
};
